/**
 * Centralized Authentication Token Service
 * Handles all token storage, retrieval, and validation
 */

import {
  TOKEN_KEY,
  TOKEN_EXPIRY_KEY,
  REFRESH_TOKEN_KEY,
  setLogoutFlag,
  clearLogoutFlag,
} from './storageKeys';

/**
 * Auth Service - Centralized token management
 */
class AuthService {
  /**
   * Store authentication token
   * @param {string} token - JWT token
   * @param {number} expiresIn - Token expiry time in seconds (optional)
   */
  setToken(token, expiresIn = null) {
    if (!token) {
      console.warn('Attempted to set empty token');
      return;
    }

    try {
      localStorage.setItem(TOKEN_KEY, token);

      // Calculate and store expiry time
      if (expiresIn) {
        const expiryTime = Date.now() + expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      } else {
        // If no expiry provided, try to decode JWT and get exp claim
        const payload = this.decodeToken(token);
        if (payload?.exp) {
          localStorage.setItem(
            TOKEN_EXPIRY_KEY,
            (payload.exp * 1000).toString()
          );
        }
      }
    } catch (error) {
      console.error('Error storing token:', error);
    }
  }

  /**
   * Get authentication token
   * @returns {string|null} Token or null if not found/expired
   */
  getToken() {
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        return null;
      }

      // Do not clear expired tokens here; let the backend and
      // axios interceptor handle 401 responses and trigger refresh.
      // We still keep authExpiry in localStorage so isTokenExpired()
      // can be used for informational purposes (e.g., warnings).
      return token;
    } catch (error) {
      console.error('Error retrieving token:', error);
      return null;
    }
  }

  /**
   * Store refresh token
   * @param {string} refreshToken
   */
  setRefreshToken(refreshToken) {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Get refresh token
   * @returns {string|null}
   */
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Clear all authentication data
   */
  clearToken() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem('goToLogout');
    } catch (error) {
      console.error('Error clearing token:', error);
    }
  }

  /**
   * Check if token exists
   * @returns {boolean}
   */
  hasToken() {
    return !!this.getToken();
  }

  /**
   * Check if token is expired
   * @returns {boolean}
   */
  isTokenExpired() {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!expiryTime) {
      // If no expiry stored, check JWT payload
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return true;

      const payload = this.decodeToken(token);
      if (payload?.exp) {
        return Date.now() >= payload.exp * 1000;
      }

      // Can't determine expiry, assume not expired
      return false;
    }

    return Date.now() >= parseInt(expiryTime, 10);
  }

  /**
   * Get time until token expires (in milliseconds)
   * @returns {number|null}
   */
  getTimeUntilExpiry() {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!expiryTime) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return null;

      const payload = this.decodeToken(token);
      if (payload?.exp) {
        return payload.exp * 1000 - Date.now();
      }
      return null;
    }

    return parseInt(expiryTime, 10) - Date.now();
  }

  /**
   * Decode JWT token (without verification - only for reading payload)
   * @param {string} token
   * @returns {object|null}
   */
  decodeToken(token) {
    try {
      if (typeof token !== 'string' || token.split('.').length < 2) {
        if (process.env.DEBUG === 'true') {
          console.error('Error decoding token: invalid token format');
        }
        return null;
      }
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      if (process.env.DEBUG === 'true') {
        console.error('Error decoding token:', error);
      }
      return null;
    }
  }

  /**
   * Get user info from token
   * @returns {object|null}
   */
  getUserFromToken() {
    const token = this.getToken();
    if (!token) return null;

    return this.decodeToken(token);
  }

  /**
   * Get authorization header
   * @returns {object|null}
   */
  getAuthHeader() {
    const token = this.getToken();
    if (!token) return null;

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Set logout flag
   */
  setLogoutFlag() {
    setLogoutFlag();
  }

  /**
   * Clear logout flag
   */
  clearLogoutFlag() {
    clearLogoutFlag();
  }

  /**
   * Check if should redirect to logout
   * @returns {boolean}
   */
  shouldLogout() {
    try {
      const goToLogout = localStorage.getItem('goToLogout');
      return goToLogout === 'true';
    } catch (_) {
      return false;
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
